import type { ApiErrorBody } from '@/types';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Thin fetch wrapper. Every API call goes through here so error shaping,
 * timeouts, the base URL and the auth header live in exactly one place.
 */

const BASE_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

/** Error carrying the backend's machine-readable code and human message. */
export class ApiError extends Error {
  readonly code: string;
  readonly detail: string | null;
  readonly retryable: boolean;
  readonly status: number;
  readonly fields: Array<{ path: string; message: string }>;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.detail = body.detail;
    this.retryable = body.retryable;
    this.fields = body.fields ?? [];
  }
}

const NETWORK_ERROR = {
  code: 'NETWORK_ERROR',
  message: 'Could not reach the analysis service.',
  detail: 'Check that the backend is running and reachable.',
  retryable: true,
};

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Abort the request after this many milliseconds. */
  timeoutMs?: number;
  /** Return the raw text body instead of parsing JSON. */
  raw?: boolean;
}

export function apiUrl(path: string): string {
  return `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as ApiErrorBody;
    if (body?.error?.code) return new ApiError(response.status, body.error);
  } catch {
    // Fall through to the generic shape below.
  }
  return new ApiError(response.status, {
    code: 'REQUEST_FAILED',
    message: 'The request could not be completed.',
    detail: `The server responded with HTTP ${response.status}.`,
    retryable: response.status >= 500,
  });
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, timeoutMs = 30_000, raw = false, headers, ...init } = options;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);

  const accessToken = useAuthStore.getState().accessToken;

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      ...init,
      signal: options.signal ?? controller.signal,
      headers: {
        accept: raw ? 'text/plain, */*' : 'application/json',
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    window.clearTimeout(timer);
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError(408, {
        code: 'TIMEOUT',
        message: 'The request took too long.',
        detail: 'Try again in a moment.',
        retryable: true,
      });
    }
    throw new ApiError(0, NETWORK_ERROR);
  } finally {
    window.clearTimeout(timer);
  }

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  if (raw) return (await response.text()) as T;

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

/** Human-readable message for any thrown value. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

export function errorDetail(error: unknown): string | null {
  return error instanceof ApiError ? error.detail : null;
}
